import { useCallback, useState } from 'react';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import { useAuthentication } from '../auth';

export type Category = {
  id: string;
  label: string;
  thumb: string;
};
export type Doc = {
  id: string;
  metadata: Record<string, string>;
  hidden?: boolean;
  category: string;
  url: string;
};
export type FilesOptions = Record<string, Doc>;

export const useMedia = () => {
  const firebase = useFirebase();
  const firestore = useFirestore();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { userId } = useAuthentication();
  const uploadMedia = useCallback(
    async ({ path, file }) => {
      setLoading(true);
      setLoaded(false);
      const { uploadTaskSnapshot } = await firebase.uploadFile(path, file);
      const ref = firebase.storage().ref(uploadTaskSnapshot.metadata.fullPath);
      const url = await ref.getDownloadURL();
      const dirtyMetadata = await ref.getMetadata();
      const metadata = JSON.parse(JSON.stringify(dirtyMetadata)); // remove undefined fields;

      await firestore.collection('media').add({ url, metadata, createdBy: userId });
      setLoading(false);
      setLoaded(true);
      return true;
    },
    [firebase, firestore, userId]
  );

  const uploadMedias = useCallback(
    async ({ path, files, filesOptions }: { path: string; files: File[]; filesOptions: FilesOptions }) => {
      setLoading(true);
      setLoaded(false);
      const batch = firestore.batch();

      const uploads = await firebase.uploadFiles(path, files);
      const filesRefs = [];
      for (const upload of uploads) {
        const ref = firebase.storage().ref(upload.uploadTaskSnapshot.metadata.fullPath);
        const url = await ref.getDownloadURL();
        const dirtyMetadata = await ref.getMetadata();
        const metadata = JSON.parse(
          JSON.stringify({
            ...dirtyMetadata,
            terms: dirtyMetadata.fullPath.split('/'),
          })
        ); // remove undefined fields;
        filesRefs.push({
          url,
          metadata,
          createdBy: userId,
          category: filesOptions[metadata.name]?.category ?? '',
          hidden: filesOptions[metadata.name]?.hidden ?? false,
          updated_at: new Date().getTime(),
          created_at: new Date().getTime(),
        });
      }

      filesRefs.map((doc) => {
        const docRef = firestore.collection('media').doc(); //automatically generate unique id
        batch.set(docRef, doc);
        return docRef;
      });
      batch.commit();
      setLoading(false);
      setLoaded(true);
      return filesRefs;
    },
    [firebase, firestore, userId]
  );

  const getMediaData = useCallback(
    async (docPath) => {
      const ref = await firestore.doc(docPath).get();
      return ref.data();
    },
    [firestore]
  );

  const deleteMedias = useCallback(
    async ({ selectedDocs }) => {
      setLoading(true);
      setLoaded(false);
      for (let file of selectedDocs) {
        await firebase.deleteFile(file.metadata.fullPath);
        await firestore.collection('media').doc(file.id).delete();
      }
      setLoading(false);
      setLoaded(true);
    },
    [firebase, firestore]
  );

  const updateMedias = useCallback(
    async ({ selectedDocs }) => {
      setLoading(true);
      setLoaded(false);
      for (let file of selectedDocs) {
        await firestore.collection('media').doc(file.id).set(file);
      }
      setLoading(false);
      setLoaded(true);
    },
    [firestore]
  );

  const createCategory = useCallback(
    async (label) => {
      await firestore.collection('categories').add({ label });
    },
    [firestore]
  );

  const updateCategory = useCallback(
    async (category) => {
      await firestore.collection('categories').doc(category.id).update(category);
    },
    [firestore]
  );

  return {
    loaded,
    loading,
    uploadMedia,
    updateMedias,
    uploadMedias,
    deleteMedias,
    getMediaData,
    createCategory,
    updateCategory,
  };
};
